import React, { useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Copy, Wand2, Hash, Type, BookOpen, FileText } from "lucide-react";
import { toast } from "sonner";

const tasks = [
  { id: "generate_post", label: "Genera post", icon: Wand2, placeholder: "Es. Vittoria del Napoli contro la Roma 3-1, gol di Lukaku e Kvaratskhelia..." },
  { id: "rewrite", label: "Riscrivi notizia", icon: BookOpen, placeholder: "Incolla qui la notizia da riscrivere..." },
  { id: "title", label: "Crea titoli", icon: Type, placeholder: "Argomento del post (es. Calciomercato Inter)" },
  { id: "hashtags", label: "Genera hashtag", icon: Hash, placeholder: "Tema del post o testo da analizzare" },
  { id: "summary", label: "Riassumi articolo", icon: FileText, placeholder: "Incolla il testo dell'articolo..." },
];

export default function AIAssistant() {
  const [task, setTask] = useState(tasks[0]);
  const [prompt, setPrompt] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!prompt.trim()) { toast.error("Inserisci un prompt"); return; }
    setLoading(true); setOutput("");
    try {
      const { data } = await api.post("/ai/run", { type: task.id, prompt });
      setOutput(data.result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setLoading(false); }
  };

  const copy = () => { navigator.clipboard.writeText(output); toast.success("Copiato"); };
  const sendToEditor = async () => {
    try {
      await api.post("/posts", { title: output.split("\n")[0].slice(0, 100), body: output, status: "draft", platforms: ["telegram"] });
      toast.success("Bozza creata!");
    } catch { toast.error("Errore"); }
  };

  return (
    <div data-testid="ai-page">
      <PageHeader title="AI Assistant" subtitle="Sfrutta GPT-5.2 per generare, riscrivere e ottimizzare i tuoi post di calcio.">
        <span className="text-xs font-mono text-blue-400 px-3 py-1.5 rounded-md bg-blue-600/10 border border-blue-500/30 ai-glow">GPT-5.2 · ATTIVO</span>
      </PageHeader>
      <PageContent>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {tasks.map(t => {
            const Icon = t.icon;
            const active = task.id === t.id;
            return (
              <button key={t.id} data-testid={`ai-task-${t.id}`} onClick={() => { setTask(t); setOutput(""); }}
                className={`p-4 rounded-md border text-left transition-all ${active ? "bg-blue-600/15 border-blue-500/50 text-blue-200 ai-glow" : "bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700"}`}>
                <Icon size={20} strokeWidth={1.75} />
                <div className="text-sm font-semibold mt-2">{t.label}</div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded-md p-5">
            <div className="text-xs uppercase tracking-wider font-mono text-slate-400 mb-3">Input</div>
            <Textarea data-testid="ai-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder={task.placeholder}
              rows={12} className="bg-slate-950 border-slate-800 resize-none font-medium" />
            <Button onClick={run} disabled={loading} data-testid="ai-run" className="mt-3 w-full bg-blue-600 hover:bg-blue-700 h-11">
              <Sparkles size={16} className="mr-2" />{loading ? "Generazione in corso..." : `Esegui · ${task.label}`}
            </Button>
          </div>

          <div className="bg-slate-900 border border-blue-500/30 rounded-md p-5 ai-glow" data-testid="ai-output-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wider font-mono text-blue-300">Risultato AI</div>
              {output && (
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={copy} data-testid="ai-copy"><Copy size={14} className="mr-1" />Copia</Button>
                  <Button size="sm" onClick={sendToEditor} data-testid="ai-to-draft" className="bg-blue-600 hover:bg-blue-700">Salva come bozza</Button>
                </div>
              )}
            </div>
            <div className="min-h-[280px] whitespace-pre-wrap text-sm text-slate-200 leading-relaxed">
              {loading ? <div className="text-slate-500">L'AI sta lavorando...</div> :
                output || <div className="text-slate-600 italic">Il risultato apparirà qui.</div>}
            </div>
          </div>
        </div>
      </PageContent>
    </div>
  );
}
